import { ref, reactive, watch } from "vue";

import type {
  SortingState,
  PaginationState,
  SortDirection,
} from "@tanstack/vue-table";

export const useServerSidePagination = (defaults: {
  pageIndex: number;
  pageSize: number;
  sortDir: SortDirection | "";
  sortKey: string;
  filter: string;
}) => {
  const paramsState = reactive({
    page: defaults.pageIndex,
    results: defaults.pageSize,
    sortDir: defaults.sortDir,
    sortKey: defaults.sortKey,
    filter: defaults.filter,
  });

  const filterState = ref(defaults.filter);
  const sortingState = ref<SortingState>(
    defaults.sortDir && defaults.sortKey
      ? [
          {
            desc: defaults.sortDir === "desc",
            id: defaults.sortKey,
          },
        ]
      : [],
  );
  const paginationState = ref<PaginationState>({
    pageIndex: defaults.pageIndex,
    pageSize: defaults.pageSize,
  });

  const resetPageIndex = () => {
    paginationState.value.pageIndex = defaults.pageIndex;
  };

  const handlePageSizeChange = (size: number) => {
    paginationState.value.pageSize = size;
    resetPageIndex();
  };

  watch(
    () => filterState.value,
    () => {
      paramsState.filter = filterState.value;
      resetPageIndex();
    },
    { immediate: true },
  );

  watch(
    () => sortingState.value,
    () => {
      if (sortingState.value.length === 0) {
        paramsState.sortDir = "";
        paramsState.sortKey = "";

        return;
      }

      const sortDir = sortingState.value[0].desc ? "desc" : "asc";
      const sortKey = sortingState.value[0].id;

      paramsState.sortDir = sortDir;
      paramsState.sortKey = sortKey;
    },
    { immediate: true, deep: true },
  );

  watch(
    () => paginationState.value,
    () => {
      // API starts pagination from 1 but tanstack-table pagination starts from 0
      paramsState.page = Number(paginationState.value.pageIndex) + 1;
      paramsState.results = paginationState.value.pageSize;
    },
    { immediate: true, deep: true },
  );

  return {
    paramsState,
    filterState,
    sortingState,
    paginationState,
    handlePageSizeChange,
    resetPageIndex,
  };
};
